/**
 * Validates email format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates Turkish phone number format: +90 5XX XXX XX XX
 */
export const validatePhone = (phone: string): boolean => {
  // Remove all spaces and special characters
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');

  // Check if it matches Turkish mobile format
  // Should start with +90 or 0 and have 5XX as the first digits
  const phoneRegex = /^(\+90|0)?5\d{9}$/;
  return phoneRegex.test(cleaned);
};

/**
 * Validates phone number - SIMPLIFIED VERSION for older users
 * Accepts any format with 10-15 digits
 */
export const validatePhoneSimple = (phone: string): boolean => {
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');

  // Check if it has between 10 and 15 digits
  return digitsOnly.length >= 10 && digitsOnly.length <= 15;
};

/**
 * Formats phone number to standard format: +90 5XX XXX XX XX
 */
export const formatPhone = (phone: string): string => {
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');

  // Remove leading +90 or 0 if present
  let digits = cleaned;
  if (digits.startsWith('+90')) {
    digits = digits.substring(3);
  } else if (digits.startsWith('0')) {
    digits = digits.substring(1);
  }

  // Format as +90 5XX XXX XX XX
  if (digits.length === 10) {
    return `+90 ${digits.substring(0, 3)} ${digits.substring(3, 6)} ${digits.substring(6, 8)} ${digits.substring(8, 10)}`;
  }

  return phone;
};

/**
 * Validates credit card number using Luhn algorithm
 */
export const validateCardNumber = (cardNumber: string): boolean => {
  // Remove spaces
  const cleaned = cardNumber.replace(/\s/g, '');

  // Check if it contains only digits and has valid length
  if (!/^\d{13,19}$/.test(cleaned)) {
    return false;
  }

  // Luhn algorithm
  let sum = 0;
  let isEven = false;

  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned.charAt(i), 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

/**
 * Validates expiry date (MM/YY format)
 */
export const validateExpiryDate = (expiry: string): boolean => {
  // Check format MM/YY
  if (!/^\d{2}\/\d{2}$/.test(expiry)) {
    return false;
  }

  const [month, year] = expiry.split('/').map(num => parseInt(num, 10));

  // Validate month
  if (month < 1 || month > 12) {
    return false;
  }

  // Check if expired
  const now = new Date();
  const currentYear = now.getFullYear() % 100; // Get last 2 digits
  const currentMonth = now.getMonth() + 1;

  if (year < currentYear || (year === currentYear && month < currentMonth)) {
    return false;
  }

  return true;
};

/**
 * Validates CVV (3-4 digits)
 */
export const validateCVV = (cvv: string, cardType?: string): boolean => {
  // American Express uses 4 digits, others use 3
  const expectedLength = cardType === 'amex' ? 4 : 3;
  const regex = new RegExp(`^\\d{${expectedLength}}$`);
  return regex.test(cvv);
};

/**
 * Formats credit card number with spaces (XXXX XXXX XXXX XXXX)
 */
export const formatCardNumber = (cardNumber: string): string => {
  const cleaned = cardNumber.replace(/\s/g, '');
  const groups = cleaned.match(/.{1,4}/g);
  return groups ? groups.join(' ') : cleaned;
};

/**
 * Formats expiry date as MM/YY
 */
export const formatExpiryDate = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');

  if (cleaned.length >= 2) {
    return `${cleaned.substring(0, 2)}/${cleaned.substring(2, 4)}`;
  }

  return cleaned;
};

/**
 * Detects card type from card number
 */
export const detectCardType = (cardNumber: string): string => {
  const cleaned = cardNumber.replace(/\s/g, '');

  if (/^4/.test(cleaned)) {
    return 'visa';
  } else if (/^5[1-5]/.test(cleaned)) {
    return 'mastercard';
  } else if (/^3[47]/.test(cleaned)) {
    return 'amex';
  } else if (/^6(?:011|5)/.test(cleaned)) {
    return 'discover';
  }

  return 'unknown';
};

/**
 * Validates amount (must be positive number)
 */
export const validateAmount = (amount: number): boolean => {
  return amount > 0 && !isNaN(amount) && isFinite(amount);
};

/**
 * Currency type
 */
export type Currency = 'EUR' | 'GBP' | 'USD' | 'TRY';

/**
 * Get currency symbol
 */
export const getCurrencySymbol = (currency: Currency): string => {
  const symbols: Record<Currency, string> = {
    EUR: '€',
    GBP: '£',
    USD: '$',
    TRY: '₺',
  };
  return symbols[currency] || '€';
};

/**
 * Get currency name
 */
export const getCurrencyName = (currency: Currency): string => {
  const names: Record<Currency, string> = {
    EUR: 'Euro',
    GBP: 'British Pound',
    USD: 'US Dollar',
    TRY: 'Turkish Lira',
  };
  return names[currency] || 'Turkish Lira';
};

/**
 * Formats currency with specified currency code
 */
export const formatCurrency = (amount: number, currency: Currency = 'EUR'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Get all available currencies
 */
export const getAvailableCurrencies = (): Array<{ code: Currency; symbol: string; name: string }> => {
  return [
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
  ];
};

/**
 * Formats date in Turkish locale (dd.MM.yyyy)
 */
export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('tr-TR');
};

/**
 * Formats date and time in Turkish locale
 */
export const formatDateTime = (date: Date): string => {
  return date.toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Validates deposit amount (must be positive and <= invoice amount)
 */
export const validateDepositAmount = (depositAmount: number, invoiceAmount: number): boolean => {
  return (
    depositAmount > 0 &&
    !isNaN(depositAmount) &&
    isFinite(depositAmount) &&
    depositAmount <= invoiceAmount
  );
};

/**
 * Validates due dates (must be valid dates and deposit due <= invoice due)
 */
export const validateDueDates = (depositDueDate: Date, invoiceDueDate: Date): boolean => {
  // Both dates must be valid
  if (!(depositDueDate instanceof Date) || isNaN(depositDueDate.getTime())) {
    return false;
  }
  if (!(invoiceDueDate instanceof Date) || isNaN(invoiceDueDate.getTime())) {
    return false;
  }

  // Deposit due date must be before or equal to invoice due date
  return depositDueDate.getTime() <= invoiceDueDate.getTime();
};

/**
 * Validates invoice number format (optional, alphanumeric with dashes)
 */
export const validateInvoiceNumber = (invoiceNumber: string): boolean => {
  if (!invoiceNumber || invoiceNumber.trim() === '') {
    return true; // Empty is valid (optional field)
  }

  // Allow alphanumeric characters, dashes, and slashes
  const invoiceRegex = /^[A-Za-z0-9\-\/]+$/;
  return invoiceRegex.test(invoiceNumber.trim()) && invoiceNumber.trim().length <= 50;
};

/**
 * Validates payment amount for partial payments
 */
export const validatePaymentAmount = (
  amount: number,
  remainingAmount: number,
  depositAmount: number,
  depositPaid: boolean
): { isValid: boolean; error?: string } => {
  // Amount must be positive
  if (amount <= 0 || isNaN(amount) || !isFinite(amount)) {
    return { isValid: false, error: 'Amount must be a positive number' };
  }

  // Amount cannot exceed remaining balance
  if (amount > remainingAmount) {
    return { isValid: false, error: `Amount cannot exceed remaining balance (${formatCurrency(remainingAmount)})` };
  }

  // If deposit not paid yet, amount must be at least deposit amount
  if (!depositPaid && amount < depositAmount) {
    return {
      isValid: false,
      error: `Minimum payment required: ${formatCurrency(depositAmount)} (deposit)`,
    };
  }

  return { isValid: true };
};

/**
 * Calculates payment type based on amount and context
 */
export const determinePaymentType = (
  amount: number,
  remainingAmount: number,
  depositAmount: number,
  depositPaid: boolean
): 'deposit' | 'partial' | 'full' => {
  // If this pays off everything
  if (amount >= remainingAmount) {
    return 'full';
  }

  // If this is the deposit payment
  if (!depositPaid && amount >= depositAmount) {
    return 'deposit';
  }

  // Otherwise it's a partial payment
  return 'partial';
};

